"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

// Issue 35.1 / 35.2 — analytics (PostHog) was firing without any
// consent gate. Add a minimum-viable opt-out banner that stores the
// choice in localStorage and dispatches a window event so
// PostHogProvider can opt-out at runtime when "decline" is chosen.
// The banner self-suppresses after a choice (accept or decline) AND
// stays out of the way for users who've already chosen.
//
// This is the bare minimum for ePrivacy / California compliance —
// a per-vendor preference center is the right shape for a v2.

const CHOICE_KEY = "ranksector.analyticsConsent";
const DISPATCH_EVENT = "ranksector:analytics-consent-changed";

type Choice = "accepted" | "declined";

function readChoice(): Choice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CHOICE_KEY);
    if (raw === "accepted" || raw === "declined") return raw;
    return null;
  } catch {
    return null;
  }
}

function writeChoice(choice: Choice) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CHOICE_KEY, choice);
    window.dispatchEvent(new CustomEvent(DISPATCH_EVENT, { detail: { choice } }));
  } catch {
    /* localStorage disabled — non-fatal */
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to next tick so SSR/CSR markup matches.
    const id = window.requestAnimationFrame(() => {
      if (readChoice() === null) setVisible(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  function choose(choice: Choice) {
    writeChoice(choice);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie and analytics consent"
      className="cookie-consent-banner"
      style={{
        background: "var(--color-primary, #1B1D21)",
        color: "#FFFFFF",
        borderRadius: 12,
        padding: "14px 18px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.24)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        maxWidth: 720,
        margin: "0 auto",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <span style={{ flex: 1, minWidth: 240 }}>
        We use analytics cookies (PostHog) to understand how Ranksector is used. No marketing cookies; we never sell your data. See our{" "}
        <Link href="/company/privacy-policy" style={{ color: "var(--color-accent-on-dark)", textDecoration: "underline" }}>
          Privacy Policy
        </Link>
        .
      </span>
      <div style={{ display: "inline-flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => choose("declined")}
          style={{
            background: "transparent",
            color: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: 6,
            padding: "8px 14px",
            minHeight: 44,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={() => choose("accepted")}
          style={{
            background: "var(--color-accent)",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 6,
            padding: "8px 16px",
            minHeight: 44,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Accept
        </button>
      </div>
    </div>
  );
}
